/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/

/**
 * Front (public marketing) vs back (console/app) is decided by the browser
 * hostname, so a single SPA build can serve both surfaces on two domains:
 *
 *   - `4allapi.com` (apex / www)  → FRONT: public marketing shell
 *   - `api.4allapi.com`, localhost, previews, IPs → BACK: full console/app
 *
 * Routes shared between the two surfaces (e.g. `/models`, `/pricing`) read
 * {@link isPublicHost} to choose their shell and auth behavior. We default
 * everything EXCEPT the known marketing apex to the back/app so that local dev
 * (`localhost`) and previews always get the full console.
 */
const PUBLIC_HOSTS: readonly string[] = [
  '4allapi.com',
  'www.4allapi.com',
  'omniapi.co',
  'www.omniapi.co',
  'aiproxys.net',
  'www.aiproxys.net',
]

/** True when the current page is served from the public marketing domain. */
export function isPublicHost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname.toLowerCase()
  return PUBLIC_HOSTS.includes(host)
}

/** True when the current page is served from the console/app domain (default). */
export function isAppHost(): boolean {
  return !isPublicHost()
}

/**
 * Apex (marketing) domain derived from the current host: api.foo.com → foo.com.
 * Lets one binary serve multiple deployments (4allapi.com, omniapi.co, …)
 * without hardcoding the marketing origin. Non-browser contexts fall back to
 * the primary deployment default.
 */
export function marketingApex(): string {
  if (typeof window === 'undefined') return '4allapi.com'
  const host = window.location.hostname
  return host.startsWith('api.') ? host.slice(4) : host
}

/**
 * Console/app origin derived from the current host: foo.com → https://api.foo.com.
 * Counterpart of {@link marketingApex} for links that must land on the console
 * subdomain regardless of which deployment served the page.
 */
export function appOrigin(): string {
  return `https://api.${marketingApex()}`
}

/**
 * 给 docs 站链接追加当前界面语言路径(docs 的语言结构:zh 在根,其余在
 * /en /ja /ko /fr /ru /vi)。仅对 docs.* 域名的根路径生效,其余原样返回。
 * 用于主站→docs 的语言联动:日语界面点"文档"应落到 docs.xxx/ja/。
 */
const DOCS_LANG_PATHS = ['en', 'ja', 'ko', 'fr', 'ru', 'vi']
export function withDocsLang(url: string, language?: string): string {
  const lang = (language || '').split('-')[0]
  if (!DOCS_LANG_PATHS.includes(lang)) return url
  try {
    const u = new URL(url)
    if (!u.hostname.startsWith('docs.')) return url
    if (u.pathname === '/' || u.pathname === '') u.pathname = `/${lang}/`
    return u.toString()
  } catch {
    return url
  }
}
